'use client'

import {createContext, Suspense, useContext, useLayoutEffect, useState, type ReactNode} from 'react'
import {usePathname, useSearchParams} from 'next/navigation'
import {useHistoryScrollRestoration} from '@fitro/overlay-layout'
import {useNavigationStackTracker} from '../../hooks/useNavigationStackTracker'

type Scope = {key: string; ready: boolean}
const ScopeContext = createContext<((scope: Scope | null) => void) | null>(null)

function ScrollRestoration({scope}: {scope: Scope | null}) {
    const pathname = usePathname()
    const searchParams = useSearchParams()
    const host = typeof window === 'undefined' ? '' : window.location.host
    useHistoryScrollRestoration({
        scopeKey: scope?.key ?? `customer-public:${host}`,
        routeKey: `${pathname ?? '/'}${searchParams?.toString() ? `?${searchParams.toString()}` : ''}`,
        enabled: scope?.ready ?? true,
    })
    return null
}

/** One scroll owner above every customer route, including public, auth and embed. */
export function CustomerScrollRestorationProvider({children}: {children: ReactNode}) {
    useNavigationStackTracker()
    const [scope, setScope] = useState<Scope | null>(null)
    return <ScopeContext.Provider value={setScope}>
        {children}
        <Suspense fallback={null}><ScrollRestoration scope={scope}/></Suspense>
    </ScopeContext.Provider>
}

/** Register a scope only while its authenticated route group is active. */
export function useCustomerScrollScope(key: string, ready: boolean) {
    const setScope = useContext(ScopeContext)
    useLayoutEffect(() => {
        setScope?.({key, ready})
        return () => setScope?.(null)
    }, [key, ready, setScope])
}
